import { Box } from '@bechtel/gds';
import { ArcElement, Chart as ChartJS, Legend } from 'chart.js';
import { cloneDeep, isUndefined, map } from 'lodash';
import { FC, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { useTheme } from 'styled-components';
import { LegendTile, MultiLingualText } from '~/components';
import { AllZeroDoghnutStyled, DoughnutTagContainer, GridContainerStyled, TagStyled } from './styled';
import { DoughnutChartDataProps } from './types';
ChartJS.register(ArcElement, Legend);

const options = {
  responsive: true,
  cutout: '60%',
  elements: {
    arc: {
      borderWidth: 0,
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: false, // <-- this option disables tooltips
    },
  },
  scales: {
    x: {
      display: false,
    },
    y: {
      display: false,
    },
  },
};
interface DoughnutChartProps {
  doughnutChartData: DoughnutChartDataProps;
  onSelectionChange: (labels: string[]) => void;
}

export const DoughnutChart: FC<DoughnutChartProps> = ({ doughnutChartData, onSelectionChange }) => {
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const { preliminaryCount, releasedCount, constructionCount, jobsiteCount, underGroundCount, aboveGroundCount } =
    doughnutChartData;
  const theme = useTheme();

  const primaryData = {
    labels: ['Preliminary', 'Jobsite Received', 'Released', 'Constructed'],
    datasets: [
      {
        label: 'Primary Status',
        data: [preliminaryCount, jobsiteCount, releasedCount, constructionCount],
        backgroundColor: [
          `${theme.colors.blue[200]}`,
          `${theme.colors.blue[300]}`,
          `${theme.colors.blue[400]}`,
          `${theme.colors.blue[500]}`,
        ],
      },
    ],
  };

  const groundData = {
    labels: ['Above Ground', 'Underground'],
    datasets: [
      {
        label: 'Location',
        data: [aboveGroundCount, underGroundCount],
        backgroundColor: [`${theme.colors.blue[300]}`, `${theme.colors.blue[400]}`],
      },
    ],
  };

  const handleSelect = (label: string) => {
    if (selectedLabels.includes(label)) {
      setSelectedLabels((prev) => prev.filter((item) => item !== label));
      onSelectionChange(selectedLabels.filter((item) => item !== label));
    } else {
      setSelectedLabels((prev) => [...prev, label]);
      onSelectionChange([...selectedLabels, label]);
    }
  };

  const doughnutData = cloneDeep(primaryData);
  doughnutData.datasets[0].data.splice(1, 1);
  doughnutData.datasets[0].data.splice(2, 0, primaryData?.datasets[0].data?.[1]);

  return (
    <>
      <DoughnutTagContainer>
        <TagStyled>
          <MultiLingualText>Primary Status</MultiLingualText>
        </TagStyled>
      </DoughnutTagContainer>
      <Box pt={{ sm: '400' }} pb={{ sm: '400' }}>
        {!doughnutData.datasets[0].data.every((item) => item === 0) ? (
          <Doughnut data={doughnutData} options={options} />
        ) : (
          <AllZeroDoghnutStyled />
        )}
      </Box>

      <GridContainerStyled hasGround={false}>
        {map(primaryData.datasets[0].data, (item, index) => (
          <LegendTile
            key={primaryData.labels[index]}
            value={item}
            label={primaryData.labels[index]}
            selected={selectedLabels.includes(primaryData.labels[index])}
            clickHandler={() => handleSelect(primaryData.labels[index])}
          />
        ))}
      </GridContainerStyled>
      {!isUndefined(aboveGroundCount || underGroundCount) && (
        <>
          <DoughnutTagContainer>
            <TagStyled>
              <MultiLingualText>Location</MultiLingualText>
            </TagStyled>
          </DoughnutTagContainer>
          <Box pt={{ sm: '400' }} pb={{ sm: '400' }}>
            {!groundData.datasets[0].data.every((item) => item === 0) ? (
              <Doughnut data={groundData} options={{ circumference: 180, rotation: -90, ...options }} />
            ) : (
              <AllZeroDoghnutStyled />
            )}
          </Box>
          <GridContainerStyled hasGround={true}>
            {map(
              groundData.datasets[0].data,
              (item, index) =>
                !isUndefined(item) && (
                  <LegendTile
                    key={groundData.labels[index]}
                    value={item}
                    label={groundData.labels[index]}
                    selected={selectedLabels.includes(groundData.labels[index])}
                    clickHandler={() => handleSelect(groundData.labels[index])}
                  />
                )
            )}
          </GridContainerStyled>
        </>
      )}
    </>
  );
};
